import {Component, OnDestroy, OnInit} from '@angular/core';
import {DcpOrder} from '@app/model/order/dcp-order.model';
import {EntryGroupService} from '@app/product/services/entry-group.service';
import {OrderConfirmationPageService} from '@app/services/order/order-confirmation-page.service';
import {DcpEntryGroup, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {OrderEntry} from '@spartacus/cart/base/root';
import {OrderFacade} from '@spartacus/order/root';
import {CmsComponentData} from '@spartacus/storefront';
import {Observable, ReplaySubject, Subscription} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {OrderItemService} from '../../order-item/services/order-item.service';
import {
  CmsFlexComponentData,
  OrderConfirmationListItemComponentTexts,
  ParsedCmsFlexComponentData,
} from './order-confirmation-order-item.model';
import {NgTemplateOutlet, AsyncPipe} from '@angular/common';
import {BundleProvidedByComponent} from '../../bundle-provided-by/bundle-provided-by.component';
import {DcpBundleOrderTableComponent} from '../../order-table/dcp-bundle-order-table/dcp-bundle-order-table.component';
import {GroupTotalComponent} from '../../group-total/group-total.component';
import {RecurringTotalComponent} from '../../recurring-total/recurring-total.component';
import {DerivativeEntryListComponent} from '../../derivative-entry-list/derivative-entry-list.component';
import {ProvidedByComponent} from '../../provided-by/provided-by.component';
import {OrderItemComponent} from '../../order-item/order-item.component';

@UntilDestroy()
@Component({
  selector: 'dcp-order-confirmation-list-item',
  templateUrl: './order-confirmation-list-item.component.html',
  styleUrls: ['./order-confirmation-list-item.component.scss'],
  imports: [
    NgTemplateOutlet,
    BundleProvidedByComponent,
    DcpBundleOrderTableComponent,
    GroupTotalComponent,
    RecurringTotalComponent,
    DerivativeEntryListComponent,
    ProvidedByComponent,
    OrderItemComponent,
    AsyncPipe,
  ],
})
export class OrderConfirmationListItemComponent implements OnInit, OnDestroy {
  public orderEntries$ = new ReplaySubject<OrderEntry[]>(1);
  public cartEntryGroups$ = new ReplaySubject<DcpEntryGroup[]>(1);
  componentData$: Observable<CmsFlexComponentData>;
  componentTexts: OrderConfirmationListItemComponentTexts;

  componentDataSubscription: Subscription = Subscription.EMPTY;

  constructor(
    private cmsData: CmsComponentData<CmsFlexComponentData>,
    private jsonService: JsonService,
    public entryGroupService: EntryGroupService,
    private orderItemService: OrderItemService,
    private orderConfirmationService: OrderConfirmationPageService,
    public orderFacade: OrderFacade,
  ) {}

  ngOnInit(): void {
    this.subscribeToComponentData();
    this.subscribeToOrder();
  }

  subscribeToOrder() {
    const savedOrderEntries = this.orderConfirmationService.getOrderEntries();
    if (savedOrderEntries) {
      this.orderEntries$.next(savedOrderEntries);
    }

    const savedCartEntryGroups = this.orderConfirmationService.getCartEntryGroups();
    if (savedCartEntryGroups) {
      this.cartEntryGroups$.next(savedCartEntryGroups);
    }

    this.orderFacade
      .getOrderDetails()
      .pipe(
        filter((order) => order != null && Object.keys(order).length > 0),
        tap((order: DcpOrder) => {
          this.orderConfirmationService.saveOrderDetails(order);
          this.orderEntries$.next(order?.entries);

          this.cartEntryGroups$.next(order.entryGroups);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Fetches component data from cloud commerce.
   */
  subscribeToComponentData(): void {
    this.componentData$ = this.cmsData.data$;
    this.componentDataSubscription = this.componentData$.pipe(untilDestroyed(this)).subscribe((d) => {
      const componentData: ParsedCmsFlexComponentData = this.jsonService.parseSafely(
        d?.classificationsProductData,
      ) as ParsedCmsFlexComponentData;
      this.componentTexts = componentData.texts;
    });
  }

  ngOnDestroy(): void {
    this.orderItemService.clearOrderItemCollapsedState();
  }
}
